import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ErrorBox } from "@/components/common/States";
import { AuthShell, Field, Submit, GoogleButton, Consents, DevModeNote } from "./shared";
import SEO from "@/components/common/SEO";

const EMPTY_C = { terms: false, kvkk: false, privacy: false };

export function validateRegister(f) {
  const e = {};
  if (!/^\S+@\S+\.\S+$/.test(f.email)) e.email = "Geçerli bir e-posta girin";
  if (f.password.length < 8 || !/\d/.test(f.password) || !/[A-Za-zÇĞİÖŞÜçğıöşü]/.test(f.password)) e.password = "En az 8 karakter, harf ve rakam içermeli";
  if (f.password !== f.password_confirm) e.password_confirm = "Şifreler eşleşmiyor";
  if (f.full_name.trim().length < 3) e.full_name = "İsim soyisim girin";
  if (!/^\+?[0-9 ()-]{10,18}$/.test(f.phone)) e.phone = "Geçerli bir telefon girin";
  return e;
}

export default function Register() {
  const nav = useNavigate();
  const [f, setF] = useState({ email: "", password: "", password_confirm: "", full_name: "", phone: "" });
  const [consents, setConsents] = useState(EMPTY_C);
  const [errors, setErrors] = useState({});
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const v = validateRegister(f);
    setErrors(v); setErr("");
    if (Object.keys(v).length) return;
    if (!consents.terms || !consents.kvkk || !consents.privacy) { setErr("Devam etmek için tüm metinleri kabul etmelisiniz"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register", { ...f, consents });
      toast.success(data.message);
      nav(`/dogrula?email=${encodeURIComponent(f.email)}${data.dev_mode ? "&dev=1" : ""}`);
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };

  return (
    <AuthShell title="Hesap oluştur" subtitle="FPV parçalarınızı ilan verin, uyumlu drone toplayın" testId="register-page">
      <SEO title="Hesap Oluştur" description="CreateFPV topluluğuna katılın, FPV parça ilanları verin ve uyumlu drone toplayın." />
      <form onSubmit={submit} className="space-y-4" noValidate>
        <Field id="register-email-input" label="E-posta" type="email" value={f.email} onChange={set("email")} error={errors.email} />
        <Field id="register-password-input" label="Şifre" type="password" value={f.password} onChange={set("password")} error={errors.password} />
        <Field id="register-password-confirm-input" label="Şifre tekrar" type="password" value={f.password_confirm} onChange={set("password_confirm")} error={errors.password_confirm} />
        <Field id="register-name-input" label="İsim soyisim" value={f.full_name} onChange={set("full_name")} error={errors.full_name} />
        <Field id="register-phone-input" label="Telefon numarası" placeholder="0532 123 45 67" value={f.phone} onChange={set("phone")} error={errors.phone} />
        <p className="text-xs text-zinc-500">Bu numara hesabınız içindir; ilanlarda farklı bir numara kullanabilirsiniz.</p>
        <Consents value={consents} onChange={setConsents} />
        {err && <ErrorBox message={err} />}
        <Submit busy={busy} testId="register-submit-button">Kayıt ol ve doğrulama kodu al</Submit>
      </form>
      <GoogleButton />
      <p className="text-sm text-zinc-500">Hesabınız var mı? <Link to="/giris" data-testid="register-login-link" className="text-[#0077ed]">Giriş yapın</Link></p>
    </AuthShell>
  );
}

export function VerifyEmail() {
  const [params] = useSearchParams();
  const { signIn } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState(params.get("email") || "");
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/verify-email", { email, code });
      signIn(data.token, data.user);
      toast.success("Hesabınız doğrulandı, hoş geldiniz!");
      nav("/");
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };
  const resend = async () => {
    try { toast.success((await api.post("/auth/resend-verification", { email })).data.message); } catch (e2) { toast.error(errMsg(e2)); }
  };
  return (
    <AuthShell title="E-postanızı doğrulayın" subtitle="E-postanıza gelen 6 haneli kodu girerek hesap açılışını tamamlayın" testId="verify-page">
      <DevModeNote show={params.get("dev") === "1"} />
      <form onSubmit={submit} className="space-y-4">
        <Field id="verify-email-input" label="E-posta" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        <Field id="verify-code-input" label="Doğrulama kodu" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} required />
        {err && <ErrorBox message={err} />}
        <Submit busy={busy} testId="verify-submit-button">Hesabı doğrula</Submit>
      </form>
      <button onClick={resend} data-testid="verify-resend-button" className="text-sm text-zinc-500 hover:text-zinc-900">Kodu tekrar gönder</button>
    </AuthShell>
  );
}
